import React, { useLayoutEffect, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, getSignedInHint } from "../../shared/auth";
import Modal, { useModalHeadSlot } from "../../shared/modal/Modal";
import SectionTabs from "../../shared/section-tabs/SectionTabs";
import LoginModal from "../../app/LoginModal";
import { isAdmin, fakeShopEnabled, freeEquipEnabled, freeEquipOwned, addFreeEquipOwned } from "../../shared/admin";
import { flagUrlSquare, flagName } from "../../shared/flags";
import { createFireworksState, drawVictoryAnim, type VictoryAnimType } from "../../game-canvas/victory-anim";
import { drawHeadStyle, headStyleRadius, type HeadStyleType } from "../../game-canvas/head-styles";
import { boardSkinById, getEquippedBoardSkin, setEquippedBoardSkin } from "../../game-canvas/board-skins";
import { makeGeo, makeSkinPainter } from "../../game-canvas/board-skin-draw";
import {
  SHOP_ITEMS, EXCLUSIVE_ITEMS, EGG_PACKS, RARITIES, type ShopItem, priceLabel, eggCost, fetchOwnedItems, ownsItem, isPurchasable, type OwnedItems,
  startCheckout, fetchSessionStatus, fakeGrant, fetchEggBalance, buyWithEggs,
} from "../../shared/shop/store";
import { readShopCache, cacheShopBalance, cacheShopOwned, clearShopCache } from "../../shared/shop/local-cache";
import { setEggBalance } from "../../shared/shop/useEggBalance";
import EggBalanceChip from "../../shared/shop/EggBalanceChip";
import { drawEggCluster, drawEmbers, eggIconUrl } from "../../shared/golden-eggs";
import { runEggCelebration, FEW_EGGS } from "./purchase-celebration";
import { runEggSpend } from "./spend-celebration";
import { runEquipTransition } from "./equip-transition";
import { setEquippedVictoryAnim, equippedVictoryAnim, setEquippedHeadStyle, equippedHeadStyle, loadLocalPlayer, saveLocalPlayer, getLocalPlayer, subscribeLocalPlayers } from "../../shared/local-players";
import DropEmitter from "../../shared/DropEmitter";
import styles from "./ShopPage.module.scss";
import { LockedRoyaleItem, UnlockMoment } from "./RoyaleItemMoments";
import { ClaimReveal, ClaimStrip } from "./RoyaleClaim";
import { useRoyaleClaimable, type CrownReward } from "../../shared/royale";
import { useNewUnlocks, markUnlockSeen } from "../../shared/shop/new-unlocks";
import { track } from "../../shared/analytics";
import musicController from "../../audio/music-controller";
import { useLandscapePhone } from "../../shared/useResponsive";

// The shop: cosmetics only, never gameplay. Victory animations preview live on
// a small canvas (the exact in-game draw code); novelty flags preview as the
// circular flag asset. Buying goes through Stripe's hosted checkout on the
// edge-server; ownership rows live in Supabase and are read back here.

// Preview canvases fill their well, so the drawing buffer must match the
// element's on-screen size (which follows the card width) or the picture gets
// scaled and its edges show as cut-offs inside the well. Returns [w, h].
function fitCanvas(canvas: HTMLCanvasElement): [number, number] {
  const w = Math.max(1, canvas.clientWidth), h = Math.max(1, canvas.clientHeight);
  if (canvas.width !== w) canvas.width = w;
  if (canvas.height !== h) canvas.height = h;
  return [w, h];
}

// The shared preview worm: the same slithering body both the head cards and
// the celebration cards use (W10 "Fast shimmer": turn length 60, speed 4.5,
// heading upper-right, body flowing in from the lower-left). Draws the body
// around head position (hx, hy); `zoom` scales only the body width (the
// wave itself is the same at every size). Returns the head's direction.
function drawPreviewWorm(ctx: CanvasRenderingContext2D, t: number, zoom: number, hx: number, hy: number): number {
  const base = -Math.PI / 4;
  const bx = Math.cos(base + Math.PI), by = Math.sin(base + Math.PI); // tailward
  const nx = -Math.sin(base), ny = Math.cos(base); // sideways
  const k = (Math.PI * 2) / 60;
  const pts: [number, number][] = [];
  // run the body until it has left the canvas (the tail must always exit the
  // visible area, whatever size the card is), with a hard cap for sanity
  const need = Math.hypot(hx, ctx.canvas.height - hy) + 60;
  for (let i = 0; i * 3.5 <= need && i < 400; i++) {
    const s = i * 3.5;
    const lat = (2 + 0.06 * s) * Math.sin(k * s - t * 4.5);
    pts.push([hx + bx * s + nx * lat, hy + by * s + ny * lat]);
  }
  ctx.lineWidth = 8 * zoom;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = "#ffcf4d";
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
  ctx.stroke();
  return Math.atan2(pts[0][1] - pts[1][1], pts[0][0] - pts[1][0]);
}

// A looping in-tile preview of a victory animation: the same slithering worm
// as the head cards (scaled down so the props have headroom), a plain head,
// and the real drawVictoryAnim on a ~3.6s loop over a continuous worm clock.
export function VictoryPreview({ type, flag }: { type: VictoryAnimType; flag?: string | null }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wtRef = useRef([...type].reduce((s, c) => s + c.charCodeAt(0), 0) % 7 * 0.9);
  // Fireworks tuning for the tiny tile: own state (a card and the confirm
  // modal can both run one) and rockets aimed with the worm (upper right,
  // the preview head doesn't actually move).
  const fwRef = useRef(createFireworksState());
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let running = true;
    let last = performance.now();
    // Zoom the whole scene in so the celebration reads in the card (less than
    // the head cards' 1.6x: the props need headroom around the worm).
    const ZOOM = 1.25, HEAD_R = headStyleRadius(4) * ZOOM;
    // What live rendering feeds drawVictoryAnim is the worm's BODY radius (4),
    // not the visible head's (body + 3): see worm-head-renderer. Mirror that
    // here (including the try-out arena's Square-700 startFxScale) so the
    // props-to-head proportion in the tile matches what you get in a match.
    const ANIM_R = 4 * ZOOM * (700 / 600);
    function loop(now: number) {
      if (!running || !ctx || !canvas) return;
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      wtRef.current += dt;
      const t = wtRef.current;
      const [W, H] = fitCanvas(canvas);
      const cx = W / 2, cy = H * 0.62;
      ctx.clearRect(0, 0, W, H);
      drawPreviewWorm(ctx, t, ZOOM, cx, cy);
      ctx.fillStyle = "#FFB74D";
      ctx.beginPath();
      ctx.arc(cx, cy, HEAD_R, 0, Math.PI * 2);
      ctx.fill();
      // Fireworks schedules its own shells on a continuous clock; wrapping it
      // at 3.6s would reset the scheduler (an instant extra launch plus a
      // replay of the shortest intervals) and make the preview visibly faster
      // than live play. The one-shot anims keep the loop.
      drawVictoryAnim(ctx, cx, cy, ANIM_R, "#ffcf4d", flag || "se", type === "fireworks" ? t : t % 3.6, type, {
        fwState: fwRef.current,
        fwHeading: -Math.PI / 4,
        // rocket travel is in prop units, so the smaller ANIM_R already pulls
        // it in; 0.9 keeps the reach where it was tuned by eye at 0.6
        fwDistScale: 0.9,
      });
      requestAnimationFrame(loop);
    }
    requestAnimationFrame(loop);
    return () => { running = false; };
  }, [type, flag]);
  return <canvas ref={canvasRef} className={styles.preview} />;
}

// Worm-head preview: a slithering worm with a FIXED heading (upper-right);
// the travelling wave is the only motion, always running.
export function HeadPreview({ type }: { type: HeadStyleType }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Animation clock; seeded per-head so the grid doesn't move in lockstep.
  const wtRef = useRef([...type].reduce((s, c) => s + c.charCodeAt(0), 0) % 7 * 0.9);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    let running = true;
    let last = performance.now();
    function loop(now: number) {
      if (!running || !ctx || !canvas) return;
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      wtRef.current += dt;
      const t = wtRef.current;
      const [W, H] = fitCanvas(canvas);
      ctx.clearRect(0, 0, W, H);
      // Zoom the worm so the head is easy to judge in the card, keeping the
      // true in-game head-to-body ratio (body radius 4 game units).
      const PREVIEW_ZOOM = 1.6;
      const dir = drawPreviewWorm(ctx, t, PREVIEW_ZOOM, W / 2, H / 2);
      drawHeadStyle(ctx, W / 2, H / 2, headStyleRadius(4, type) * PREVIEW_ZOOM, dir, t, type, "#ffcf4d"); // (the preview worm's colour)
      requestAnimationFrame(loop);
    }
    requestAnimationFrame(loop);
    return () => { running = false; };
  }, [type]);
  return <canvas ref={canvasRef} className={styles.preview} />;
}

// Board preview: the real thing, cropped. The skin is painted at the game's
// true size (a 700-unit arena with its 10-unit wall at 1px/unit, exactly what
// the map renderer draws) into an offscreen canvas, and the card shows the
// top-left corner of it: two walls, a corner cobweb / torch where the skin
// has them, at near 1:1 so line work stays crisp. No worms: the arena alone.
// `size`: draw an exact square of that many px with no margins (the confirm
// modal); default is the 168x150 card canvas with the square centred.
export function BoardPreview({ id, size }: { id: string; size?: number }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx || !boardSkinById(id)) return;
    const FULL = 720, WALL = 10;                  // game geometry at 1px per unit
    const G = makeGeo("square", FULL, WALL, 1);
    const painter = makeSkinPainter(id, G);
    if (!painter) return;
    if (size) { canvas.width = size; canvas.height = size; }
    const off = document.createElement("canvas"); off.width = FULL; off.height = FULL;
    const octx = off.getContext("2d")!;
    const CROP = 205;                              // source window height (px), shown at ~0.8x in the card
    let running = true;
    const start = performance.now();
    const paint = (now: number) => {
      const t = (now - start) / 1000;
      octx.clearRect(0, 0, FULL, FULL);
      painter.paintFloor(octx, t);
      painter.paintWall(octx, t);
      // Fill the whole canvas: the source window keeps the card's aspect (a
      // wider card just shows more of the arena), anchored at the corner. In
      // the card the well has no padding; the breathing room lives INSIDE the
      // canvas instead, a 10px inset on ALL sides so the arena sits centered,
      // and a future skin whose details overhang the wall has room to draw.
      const [W, H] = size ? [size, size] : fitCanvas(canvas);
      const PAD = size ? 0 : 10;
      const w = W - 2 * PAD, h = H - 2 * PAD;
      const scale = Math.max(h / CROP, w / FULL);
      ctx.clearRect(0, 0, W, H);
      ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = "high";
      ctx.drawImage(off, 0, 0, w / scale, h / scale, PAD, PAD, w, h);
    };
    if (painter.animated) {
      const loop = (now: number) => { if (!running) return; paint(now); requestAnimationFrame(loop); };
      requestAnimationFrame(loop);
    } else {
      // Static skins paint once, and again whenever the card changes size.
      paint(start);
      const ro = new ResizeObserver(() => paint(performance.now()));
      ro.observe(canvas);
      return () => { running = false; ro.disconnect(); };
    }
    return () => { running = false; };
  }, [id, size]);
  return <canvas ref={canvasRef} className={size ? styles.preview + " " + styles.previewFixed : styles.preview} />;
}

// THE shop card: packs and cosmetics/effects render through this one
// component, so they share width, height, chrome, and the tier/lvl hover
// ladder exactly. The well has a fixed height, which is what keeps a pack
// (egg cluster + amount) and a cosmetic (preview only) the same size.
const PACK_TIERS = ["tierBlack", "tierTurquoise", "tierPurple", "tierGold"] as const;
const PACK_LVLS = ["lvl1", "lvl2", "lvl3", "lvl4"] as const;

function ShopCard({ title, tier, lvl, equipped, tag, tagClass, visual, amount, caption, actions, onClick, onHover, bareWell, dataId, frameClass }: {
  title: string;
  tier: string;
  lvl: string;
  equipped?: boolean;
  tag?: string;
  tagClass?: string;
  visual: React.ReactNode;
  amount?: string;
  caption?: string;
  actions: React.ReactNode;
  onClick?: () => void;
  onHover?: (hovering: boolean) => void;
  bareWell?: boolean; // arena cards: the preview IS the well (no chrome)
  dataId?: string; // data-packid hook for the purchase celebration
  frameClass?: string; // an extra class on the card (the Royale Laurel's gold frame)
}) {
  return (
    // The slot is the fixed-size hover target; the card inside zooms. Keeping
    // the hover hit area constant is what stops the zoom from jittering.
    <div className={styles.cardSlot} data-cardslot="" data-packid={dataId}>
      <div
        className={[styles.pack, styles[tier], styles[lvl], equipped ? styles.cardEquipped : "", frameClass || ""].filter(Boolean).join(" ")}
        data-pack=""
        role={onClick ? "button" : undefined}
        onClick={onClick}
        onMouseEnter={onHover ? () => onHover(true) : undefined}
        onMouseLeave={onHover ? () => onHover(false) : undefined}
      >
        <FitTitle text={title} />
        <div className={bareWell ? `${styles.well} ${styles.wellBare}` : styles.well}>
          {tag ? <span className={tagClass}>{tag}</span> : null}
          {visual}
          {amount ? <div className={styles.amt} data-amt="">{amount}</div> : null}
          {caption ? <div className={styles.cur}>{caption}</div> : null}
        </div>
        {actions}
      </div>
    </div>
  );
}

// The one buy button every card uses: gold pill with the drop emitter behind
// it. Packs pass a price label, cosmetics an egg cost. Stops propagation so a
// click doesn't also fire the card's own onClick.
function BuyButton({ busy, onClick, children }: { busy: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <span className={styles.buyWrap}>
      <DropEmitter hostClass={styles.dropHost} dropClass={styles.drop} />
      <button className={styles.buy} data-buy="" disabled={busy} onClick={(e) => { e.stopPropagation(); onClick(); }}>
        {children}
      </button>
    </span>
  );
}

// A pack's live egg-cluster canvas (the top tier also gets embers on hover).
function EggPackCard({ pack, index, busy, onBuy }: { pack: ShopItem; index: number; busy: boolean; onBuy: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const hoverAt = useRef(-1);
  useEffect(() => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    let running = true;
    const start = performance.now();
    const count = Math.min(4, index + 1);
    function loop(now: number) {
      if (!running || !ctx) return;
      const t = (now - start) / 1000 + index * 0.6;
      const hoverT = hoverAt.current >= 0 ? (now - hoverAt.current) / 1000 : -1;
      drawEggCluster(ctx, 120, t, count, hoverT);
      if (index === 3 && hoverT >= 0) drawEmbers(ctx, 120, t);
      requestAnimationFrame(loop);
    }
    requestAnimationFrame(loop);
    return () => { running = false; };
  }, [index]);
  return (
    <ShopCard
      title={pack.name || pack.label}
      dataId={pack.id}
      tier={PACK_TIERS[index]}
      lvl={PACK_LVLS[index]}
      tag={pack.bonus ? `+${pack.bonus.toLocaleString()} eggs` : undefined}
      tagClass={styles.btag}
      visual={<canvas ref={canvasRef} width={120} height={120} />}
      amount={(pack.eggs || 0).toLocaleString()}
      caption="Golden Eggs"
      onClick={onBuy}
      onHover={(h) => { hoverAt.current = h ? performance.now() : -1; }}
      actions={<BuyButton busy={busy} onClick={onBuy}>{priceLabel(pack)}</BuyButton>}
    />
  );
}

// One cosmetic/effect card: same ShopCard as the packs, with the rarity
// driving the tier wash + hover level. Tracks hover so previews that animate
// on hover (the worm heads) know when to run.
function CosmeticCard({ item, equipped, actions, onClick, renderPreview }: {
  item: ShopItem;
  equipped: boolean;
  actions: React.ReactNode;
  onClick?: () => void;
  renderPreview: (active: boolean) => React.ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  const [tierCls, lvlCls] = RARITY_TIER[item.rarity || "common"];
  return (
    <ShopCard
      dataId={item.id}
      title={item.label}
      tier={tierCls}
      lvl={lvlCls}
      equipped={equipped}
      bareWell={item.kind === "board"}
      onClick={onClick}
      visual={renderPreview(hovered)}
      actions={actions}
      onHover={setHovered}
    />
  );
}

// Card heading that always fits on one line: measures the text at the base
// size and scales the font down just enough (letter-spacing with it) so a
// long label like "Champagne Shower" is written in full instead of cut off.
// Re-measures on resize, since card widths follow the grid.
function FitTitle({ text }: { text: string }) {
  const ref = useRef<HTMLHeadingElement | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const BASE = 12.5, BASE_LS = 2, MIN = 8;
    const fit = () => {
      el.style.fontSize = `${BASE}px`;
      el.style.letterSpacing = `${BASE_LS}px`;
      const avail = el.clientWidth;
      // scrollWidth already reflects the ellipsis-clipped run, so measure the
      // text itself through a Range.
      const range = document.createRange();
      range.selectNodeContents(el);
      const need = Math.ceil(range.getBoundingClientRect().width) + 1;
      if (avail > 0 && need > avail) {
        const k = Math.max(MIN / BASE, avail / need);
        el.style.fontSize = `${(BASE * k).toFixed(2)}px`;
        el.style.letterSpacing = `${(BASE_LS * k).toFixed(2)}px`;
      }
    };
    fit();
    // The webfont (Orbitron) usually lands after first paint and is wider than
    // the fallback, so measure again once fonts are in.
    let alive = true;
    document.fonts?.ready.then(() => { if (alive) fit(); });
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => { alive = false; ro.disconnect(); };
  }, [text]);
  return <h4 ref={ref}>{text}</h4>;
}



// Rarity rides the same tier ladder as the packs: colour wash + escalating
// hover come from the shared tier/lvl classes.
const RARITY_TIER: Record<string, [string, string]> = {
  common: ["tierBlack", "lvl1"],
  rare: ["tierTurquoise", "lvl2"],
  epic: ["tierPurple", "lvl3"],
  legendary: ["tierGold", "lvl4"],
};

// Per-item lines for the purchase confirmation; anything not listed falls
// back to the generic line for its kind.
// The department icons of the landscape phone's left rail.
const railSvg = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
const RAIL_ICONS: Record<string, React.ReactElement> = {
  packs: <svg {...railSvg}><path d="M12 3c3 0 5.5 4.6 5.5 8.5a5.5 5.5 0 01-11 0C6.5 7.6 9 3 12 3z" /><path d="M5 20h14" /></svg>,
  heads: <svg {...railSvg}><path d="M3 18c4 0 3-7 7-7h3" /><circle cx="17" cy="11" r="4" fill="currentColor" fillOpacity="0.25" /></svg>,
  arena: <svg {...railSvg}><rect x="3" y="6" width="18" height="12" rx="6" /><path d="M8 12c2-2 3 2 5 0s2-1 3 0" /></svg>,
  flags: <svg {...railSvg}><path d="M5 21V4M5 5h13l-3 4 3 4H5" fill="currentColor" fillOpacity="0.2" /></svg>,
  victory: <svg {...railSvg}><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M18 6l-2.5 2.5M8.5 15.5L6 18" /></svg>,
};
// The item sheet's accent, per rarity (the same ladder as the card washes).
const RARITY_COLOR: Record<string, string> = { common: "#9fb0c8", rare: "#28c4d4", epic: "#ac5cff", legendary: "#ffcf4d" };

const CONFIRM_BLURBS: Record<string, string> = {
  gold: "It's like buying a Rolex.",
  pacman: "Notice that this is Pac-Worm. So no copyright issues.",
  goldegg: "Show them your wallet! Ehum I mean skills!",
  rocket: "Who doesn't appreciate a clean looking space ship?",
  dungeon: "A bit spooky. But also kind of cozy.",
  goldenegg: "Just 1000 golden eggs for 1 golden egg. What a catch!",
  champagne: "Spray it like you just won Monaco.",
};

// The shop's departments: the currency packs first, then one tab per
// cosmetic category (flat beats nesting while there are only four).
type ShopTab = "packs" | "heads" | "arena" | "flags" | "victory";
const SHOP_TABS: { id: ShopTab; label: string }[] = [
  { id: "packs", label: "Egg Packs" },
  { id: "heads", label: "Worm Heads" },
  { id: "arena", label: "Arena" },
  { id: "flags", label: "Flags" },
  { id: "victory", label: "Celebrations" },
];

// The line under an item's name when you are asked to confirm buying it.
function confirmBlurb(item: ShopItem): string {
  return CONFIRM_BLURBS[item.id] ? CONFIRM_BLURBS[item.id]
    : item.kind === "head" ? "This new head will for sure help you win games. I promise."
    : item.kind === "board" ? "If you are spending time in this game you might as well make it pretty."
    : item.kind === "flag" ? "This flag is so you."
    : "You know, I can't guarantee that you will ever get to see it.";
}

export default function ShopPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  // Won items: the locked one's "Nice try", and the unlock moment of one won and not seen yet.
  const newUnlocks = useNewUnlocks();
  const [lockedItem, setLockedItem] = useState<ShopItem | null>(null);
  const [unlockItem, setUnlockItem] = useState<ShopItem | null>(null);
  // A Royale win's reward waiting to be claimed (the win screen sends you
  // here), and the one being claimed now (its reveal over the screen).
  const claimable = useRoyaleClaimable();
  const [claiming, setClaiming] = useState<CrownReward | null>(null);
  // A landscape phone: the departments are a rail on the left, the cards a
  // compact shelf, and buying happens in a sheet from the right (see the
  // `app` branches below and .app in the stylesheet).
  const app = useLandscapePhone();
  const shelfRef = useRef<HTMLDivElement | null>(null);
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<ShopTab>(() => {
    const t = params.get("tab");
    return (SHOP_TABS.some((x) => x.id === t) ? t : "packs") as ShopTab;
  });
  // The category lives in the URL (?tab=..; the default packs keeps it clean),
  // so a refresh or the sign-in round-trip lands back on the same tab.
  const selectTab = (t: ShopTab) => {
    setTab(t);
    setParams(t === "packs" ? {} : { tab: t }, { replace: true });
  };
  // ...and the URL can change under the page (the egg pill in a landscape
  // phone's top bar leads to /shop, the Egg Packs): the tab follows it.
  useEffect(() => {
    const t = params.get("tab");
    const next = (SHOP_TABS.some((x) => x.id === t) ? t : "packs") as ShopTab;
    setTab((cur) => (cur === next ? cur : next));
  }, [params]);
  // Purchase confirmation: the cosmetic waiting for a yes (null = closed).
  const [pending, setPending] = useState<ShopItem | null>(null);
  // Signed-out clicks anywhere in the shop land here: the real sign-in modal.
  const [showLogin, setShowLogin] = useState(false);
  // Optimistic first paint: seed balance + owned from the last-known cache
  // when the signed-in hint says this browser was signed in; the real fetches
  // below correct it, and a different-account resolve resets it first.
  const [signedInHint] = useState(getSignedInHint);
  const [seed] = useState(() => (signedInHint ? readShopCache() : null));
  const probablySignedIn = authLoading ? signedInHint : !!user;
  const [owned, setOwned] = useState<OwnedItems | null>(seed?.owned ?? null);
  // Whose account `owned` / `eggBalance` currently describe: the seed's until
  // a server fetch lands. Gates the cache writes so a stale snapshot of one
  // account is never re-labelled as another's.
  const stateUidRef = useRef<string | null>(seed?.uid ?? null);
  const [banner, setBanner] = useState<{ kind: "good" | "info"; text: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [equipped, setEquipped] = useState<string | null>(() => equippedVictoryAnim());
  const [equippedFlag, setEquippedFlagState] = useState<string | null>(() => loadLocalPlayer(0)?.flag ?? null);
  const [equippedHead, setEquippedHead] = useState<string | null>(() => equippedHeadStyle());
  const [equippedBoard, setEquippedBoard] = useState<string | null>(() => getEquippedBoardSkin());
  // Equips can change underneath the page (the account-settings download on
  // sign-in replaces the local player slots): re-read so the badges follow.
  useEffect(() => subscribeLocalPlayers(() => {
    setEquipped(equippedVictoryAnim());
    setEquippedHead(equippedHeadStyle());
    setEquippedFlagState(loadLocalPlayer(0)?.flag ?? null);
  }), []);
  // Admin test mode lives on the Admin page now (no admin chrome in the shop).
  const fakeMode = isAdmin(user) && fakeShopEnabled();
  const freeEquip = isAdmin(user) && freeEquipEnabled();
  // Pretend purchases made in free-equip mode (admin only, local, see admin.ts).
  const [testOwned, setTestOwned] = useState<Set<string>>(() => freeEquipOwned());
  const isOwned = (item: ShopItem) => ownsItem(owned, item.kind, item.id) || (freeEquip && testOwned.has(item.id));
  const [eggBalance, setEggBalance] = useState<number | null>(seed?.balance ?? null);
  // Transient banners self-dismiss; they live as compact chips in the modal
  // head next to the title instead of full-width alert rows.
  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => setBanner(null), 5000);
    return () => clearTimeout(t);
  }, [banner]);
  // (A landscape phone's top bar has the egg pill instead, and the notice
  // shows as a toast over the shelf.)
  useModalHeadSlot(
    app ? null : <>
      {banner ? (
        <span className={`${styles.hchip} ${banner.kind === "good" ? styles.hchipGood : styles.hchipInfo}`} title={banner.text}>
          {banner.text}
        </span>
      ) : null}
      {fakeMode ? <span className={`${styles.hchip} ${styles.hchipInfo}`} title="Shop test mode is on: purchases are free and skip payment. Turn it off in Admin tools.">TEST MODE</span> : null}
      {freeEquip ? <span className={`${styles.hchip} ${styles.hchipInfo}`} title="Free-equip mode is on: every cosmetic can be equipped without buying it. Turn it off in Admin tools.">FREE EQUIP</span> : null}
      {probablySignedIn && eggBalance !== null ? <EggBalanceChip value={eggBalance} /> : null}
    </>,
  );

  // Run the purchase celebration for an egg grant of `total`, advancing the
  // visible balance as eggs land. `packId` locates the source card (falls
  // back gracefully when the tab changed before the redirect came back).
  const celebrate = React.useCallback((packId: string | null, total: number, startBalance: number) => {
    const slot = packId ? document.querySelector<HTMLElement>(`[data-packid="${packId}"]`) : null;
    let shown = startBalance;
    let raf = 0;
    const flush = () => { setEggBalance(shown); raf = 0; };
    const packDef = packId ? EGG_PACKS.find((pk) => pk.id === packId) : undefined;
    if (total > FEW_EGGS) musicController.play("eggs");
    return runEggCelebration({
      slot,
      total,
      buttonLabel: packDef ? priceLabel(packDef) : undefined,
      packName: packDef?.name,
      onProgress: (landed) => {
        shown = startBalance + landed;
        if (!raf) raf = requestAnimationFrame(flush);
      },
    }).then(() => { setEggBalance(startBalance + total); });
  }, []);

  // The spend celebration: eggs stream from the chip back behind the item's
  // buy button; ownership flips at the reveal. Balance follows departed eggs.
  const celebrateSpend = React.useCallback((itemId: string, itemLabel: string, cost: number, startBalance: number, onOwn?: () => void) => {
    const slot = document.querySelector<HTMLElement>(`[data-packid="${itemId}"]`);
    let shown = startBalance;
    let raf = 0;
    const flush = () => { setEggBalance(shown); raf = 0; };
    musicController.play("ui-buy");
    return runEggSpend({
      slot,
      cost,
      itemLabel,
      onProgress: (departed) => {
        shown = startBalance - departed;
        if (!raf) raf = requestAnimationFrame(flush);
      },
      onOwn: onOwn ?? (() => { refreshOwnedRef.current(); }),
    }).then(() => { setEggBalance(startBalance - cost); });
  }, []);
  const refreshOwnedRef = useRef<() => void>(() => {});

  // Fetches are tagged with the account they were made for and dropped if it
  // changed underneath them (sign-out, account switch) before they landed.
  const userIdRef = useRef<string | null>(null);
  userIdRef.current = user?.id ?? null;
  const refreshOwned = React.useCallback(() => {
    const uid = userIdRef.current;
    if (!uid) return;
    fetchOwnedItems().then((o) => {
      if (userIdRef.current !== uid) return;
      stateUidRef.current = uid;
      setOwned(o);
    });
  }, []);
  useEffect(() => { refreshOwnedRef.current = refreshOwned; }, [refreshOwned]);
  // Back from Stripe with a celebration still to play: the displayed balance
  // holds at its pre-purchase value until the count-up starts. Otherwise the
  // routine fetch (which already includes the grant) shows the new total the
  // moment auth resolves, and the celebration then appears to roll it back.
  const holdEggsRef = useRef(false);
  const refreshEggs = React.useCallback(() => {
    const uid = userIdRef.current;
    if (!uid) return;
    fetchEggBalance().then((n) => {
      if (userIdRef.current !== uid) return;
      stateUidRef.current = uid;
      if (holdEggsRef.current) return;
      setEggBalance(n);
    });
  }, []);
  useEffect(() => {
    // Until the session is known, keep painting the seeded snapshot (an
    // unauthenticated fetch would come back empty and wipe it).
    if (authLoading) return;
    if (!user) {
      stateUidRef.current = null;
      setOwned(null);
      setEggBalance(null);
      clearShopCache();
      return;
    }
    // Resolved as a different account than the snapshot: blank first, so the
    // other player's eggs and lockers never flash on this one's page.
    if (stateUidRef.current !== user.id) {
      setOwned(null);
      setEggBalance(null);
    }
    refreshOwned();
    refreshEggs();
  }, [authLoading, user?.id, refreshOwned, refreshEggs]); // eslint-disable-line react-hooks/exhaustive-deps
  // Keep the snapshot current: server fetches and the purchase / spend flows
  // all land in these two states, so this catches every change.
  useEffect(() => {
    if (!user || stateUidRef.current !== user.id) return;
    cacheShopOwned(user.id, owned);
    if (eggBalance !== null) cacheShopBalance(user.id, eggBalance);
    // ... and the golden Shop button elsewhere follows (shared/shop/useEggBalance).
    if (eggBalance !== null) setEggBalance(eggBalance);
  }, [user?.id, owned, eggBalance]); // eslint-disable-line react-hooks/exhaustive-deps

  // Back from a try-out match, or sent here by a locked cosmetic elsewhere
  // (the flag picker): /shop?tab=..&confirm=<id> opens that item's purchase
  // confirmation. Same gate as clicking its card: signed out gets the login
  // modal instead, so we wait for auth to settle before deciding.
  useEffect(() => {
    if (authLoading) return;
    const confirmId = params.get("confirm");
    if (!confirmId) return;
    const item = SHOP_ITEMS.find((i) => i.id === confirmId && i.kind !== "eggs");
    const t = params.get("tab"); // consume confirm, keep the category URL
    setParams(t ? { tab: t } : {}, { replace: true });
    if (!item) return;
    if (!user) setShowLogin(true);
    else setPending(item);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  // Return from Stripe: ?purchase=success&session_id=... - poll until the
  // webhook (or the status check itself) has granted the item.
  useEffect(() => {
    const purchase = params.get("purchase");
    if (!purchase) return;
    const sessionId = params.get("session_id");
    const t = params.get("tab"); // consume the Stripe params, keep the category URL
    setParams(t ? { tab: t } : {}, { replace: true });
    // Backing out of checkout is a non-event: no alert, just back to browsing.
    if (purchase === "cancel") return;
    if (purchase === "success" && sessionId) {
      setBanner({ kind: "info", text: "Finishing your purchase…" });
      holdEggsRef.current = true;
      const release = () => { holdEggsRef.current = false; refreshEggs(); };
      let attempts = 0;
      const poll = async () => {
        attempts++;
        const status = await fetchSessionStatus(sessionId);
        if (status.owned) {
          track("Purchase", { eggs: (status as { eggs?: number }).eggs || 0 });
          setBanner({ kind: "good", text: "Purchase complete. It's yours!" });
          refreshOwned();
          const granted = (status as { eggs?: number }).eggs || 0;
          if (granted > 0) {
            const pack = EGG_PACKS.find((pk) => (pk.eggs || 0) === granted);
            // This effect mounts before the balance has loaded, so the state
            // value here is stale (null on a fresh return from Stripe). Fetch
            // the real ledger balance NOW (it already includes this grant) and
            // count up from just below it, not from 0.
            const bal = await fetchEggBalance();
            const start = Math.max(0, (bal ?? granted) - granted);
            await celebrate(pack?.id ?? null, granted, start);
          }
          release();
          return;
        }
        if (attempts < 6) setTimeout(poll, 1500);
        else {
          setBanner({ kind: "info", text: "Payment received. Your item will appear shortly." });
          release();
        }
      };
      poll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function buy(item: ShopItem) {
    if (busyId) return;
    if (!user) {
      setShowLogin(true);
      return;
    }
    if (freeEquip && item.kind !== "eggs") {
      // Admin free-equip: behave exactly like a completed purchase, locally -
      // celebration included; the pretend grant lands at the reveal moment.
      await celebrateSpend(item.id, item.label, eggCost(item), eggBalance ?? 0, () => {
        setTestOwned(addFreeEquipOwned(item.id));
      });
      refreshEggs(); // nothing was actually spent; restore the true balance
      // No auto-equip: equipping is its own deliberate (and fun) click.
      return;
    }
    setBusyId(item.id);
    try {
      if (fakeMode) {
        const res = await fakeGrant(item.id);
        if (res.owned) {
          setBanner({ kind: "good", text: `${item.label} granted (admin test).` });
          if (item.kind === "eggs") {
            await celebrate(item.id, (res as { eggs?: number }).eggs || item.eggs || 0, eggBalance ?? 0);
            refreshEggs();
          } else {
            await celebrateSpend(item.id, item.label, eggCost(item), eggBalance ?? 0);
            refreshEggs();
          }
        } else {
          setBanner({ kind: "info", text: "Test grant failed: " + (res.error || "unknown") });
          musicController.play("ui-error");
        }
        return;
      }
      if (item.kind === "eggs") {
        // Packs are the only real-money purchase: Stripe hosted checkout.
        const res = await startCheckout(item.id);
        if (res.url) {
          window.location.href = res.url; // Stripe's hosted checkout page
          return;
        }
        if (res.error) musicController.play("ui-error");
        if (res.error === "shop_unconfigured") {
          setBanner({ kind: "info", text: "The shop isn't taking payments right now. Try again later." });
        } else if (res.error) {
          setBanner({ kind: "info", text: "Could not start checkout (" + res.error + ")." });
        }
        return;
      }
      // Cosmetics/effects are bought with Golden Eggs.
      const res = await buyWithEggs(item.id);
      if (res.owned) {
        setBanner({ kind: "good", text: `${item.label} is yours! (${(res.spent ?? eggCost(item)).toLocaleString()} Golden Eggs)` });
        await celebrateSpend(item.id, item.label, res.spent ?? eggCost(item), eggBalance ?? 0);
        refreshEggs();
      } else if (res.alreadyOwned) {
        setBanner({ kind: "good", text: "You already own that one." });
        refreshOwned();
      } else if (res.error === "not_enough_eggs") {
        musicController.play("ui-error");
        setBanner({
          kind: "info",
          text: `Not enough Golden Eggs: ${item.label} costs ${res.cost.toLocaleString()} and you have ${res.balance.toLocaleString()}. Grab a pack!`,
        });
        selectTab("packs");
      } else if (res.error) {
        setBanner({ kind: "info", text: "Could not complete the purchase (" + res.error + ")." });
        musicController.play("ui-error");
      }
    } finally {
      setBusyId(null);
    }
  }

  function equipVictory(item: ShopItem) {
    const next = equipped === item.id ? null : item.id;
    setEquippedVictoryAnim(next);
    setEquipped(next);
  }

  function equipHead(item: ShopItem) {
    const next = equippedHead === item.id ? null : item.id;
    setEquippedHeadStyle(next);
    setEquippedHead(next);
  }

  function equipBoard(item: ShopItem) {
    const next = equippedBoard === item.id ? null : item.id;
    setEquippedBoardSkin(next);
    setEquippedBoard(next);
  }

  function equipFlag(item: ShopItem) {
    const p = getLocalPlayer(0, "You");
    saveLocalPlayer(0, { ...p, flag: item.id });
    setEquippedFlagState(item.id);
  }

  const equippedOfKind: Record<string, string | null> = { victory: equipped, flag: equippedFlag, head: equippedHead, board: equippedBoard };
  const equipFn: Record<string, (i: ShopItem) => void> = { victory: equipVictory, flag: equipFlag, head: equipHead, board: equipBoard };
  // Equips live in local storage (account-independent), but the shop's green
  // "Equipped" dressing only makes sense next to verified ownership: signed
  // out, every card reads as buyable, so show none of it.
  const isEquippedItem = (item: ShopItem) => probablySignedIn && equippedOfKind[item.kind] === item.id;
  // What is currently equipped for a given kind (null = nothing).
  const currentEquippedId = (kind: string): string | null =>
    kind === "victory" ? equipped : kind === "head" ? equippedHead : kind === "board" ? equippedBoard : kind === "flag" ? equippedFlag : null;

  const equipItem = (item: ShopItem) => {
    // (Free-equip pretend purchases stay "owned" until the mode is switched
    // off, so equip/de-equip can be toggled freely while testing.)
    // Free-equip is local pretend, but ONLINE matches verify real ownership
    // (server-side gate) - so also fire the admin fake-grant in the
    // background, making the equipped item actually show in online games.
    if (freeEquip && !isEquippedItem(item) && isPurchasable(item.kind, item.id) && !ownsItem(owned, item.kind, item.id)) {
      void fakeGrant(item.id).then((r) => { if (r?.owned) refreshOwned(); });
    }
    const equipping = !isEquippedItem(item);
    // Swapping within a kind: the previously equipped card plays its grey
    // de-equip scanline in parallel. Its snapshot is taken BEFORE the flip.
    const prevId = currentEquippedId(item.kind);
    if (equipping && prevId && prevId !== item.id) {
      runEquipTransition(prevId, false, () => {});
    }
    // The clicked card: green sweep down to equip, grey sweep up to de-equip.
    runEquipTransition(item.id, equipping, () => equipFn[item.kind](item));
  };

  // The whole card is a button too: clicking anywhere does what its action
  // button does (equip toggle when owned, buy otherwise). The buttons stop
  // propagation so a click on them doesn't fire twice.
  // Cosmetics go through a confirmation first (with a "try it" for heads,
  // arenas and celebrations); egg packs go straight to checkout.
  function askToBuy(item: ShopItem) {
    // A click in the brief pre-session window is ignored rather than met with
    // a sign-in modal the (probably signed-in) player doesn't need.
    if (!user) { if (!authLoading) setShowLogin(true); return; }
    if (busyId) return;
    setPending(item);
  }
  function tileAction(item: ShopItem): (() => void) | undefined {
    // Signed out, every card is a "sign in first" prompt: the real sign-in
    // modal, not a easily-missed chip.
    if (!user) return authLoading ? undefined : () => setShowLogin(true);
    if (isOwned(item)) return () => equipItem(item);
    return () => askToBuy(item);
  }

  // Signed out, cards render exactly as for a signed-in browser (prices and
  // all); the sign-in ask happens on click, via the buy path's login modal.
  function tileActions(item: ShopItem) {
    if (isOwned(item)) {
      // Owned: the price slot becomes the equip toggle. Solid when equipped
      // (click to take it off), outlined when it's just sitting in the locker.
      const on = isEquippedItem(item);
      const label = on ? "Equipped" : "Equip";
      // Same wrapper as the buy button so the card's last-child margin rule
      // treats both rows identically (no 1px shuffle when they swap).
      return (
        <span className={styles.buyWrap}>
          <button
            className={on ? `${styles.equip} ${styles.equipOn}` : styles.equip}
            aria-pressed={on}
            onClick={(e) => { e.stopPropagation(); equipItem(item); }}
          >
            {label}
          </button>
        </span>
      );
    }
    // Not owned: the buy button. (Admins can test via the Admin page toggles:
    // fake shop grants for real, free-equip pretends locally; either way the
    // card looks the same as for any player.)
    return (
      <BuyButton busy={busyId === item.id} onClick={() => askToBuy(item)}>
        <img src={eggIconUrl()} alt="" className={styles.eggIcon} />
        {eggCost(item).toLocaleString()}
      </BuyButton>
    );
  }

  // Every department lists cheapest first, whatever order the catalog keeps.
  const ofKind = (kind: string) => SHOP_ITEMS.filter((i) => i.kind === kind).sort((a, b) => eggCost(a) - eggCost(b));
  const victoryItems = ofKind("victory");
  const flagItems = ofKind("flag");
  const headItems = ofKind("head");
  const boardItems = ofKind("board");
  // Not for sale: the Royale Laurel comes with your first Royale win. It sits
  // first on the Worm Heads shelf, gold-framed; once it is yours (the same
  // ownership row a bought head has) it is a head like any other.
  const royaleHeads: ShopItem[] = EXCLUSIVE_ITEMS.filter((i) => i.kind === "head");
  const headCard = (item: ShopItem) => (
    <CosmeticCard key={item.id} item={item} onClick={tileAction(item)} equipped={isEquippedItem(item)} actions={tileActions(item)} renderPreview={() => <HeadPreview type={item.id as HeadStyleType} />} />
  );
  // In the item sheet: can the player pay for it? (Unknown balance: let the
  // server answer. The admin test modes never charge.)
  const short = (item: ShopItem) => (fakeMode || freeEquip || eggBalance === null ? 0 : Math.max(0, eggCost(item) - eggBalance));



  return (
    <div className={app ? `${styles.root} ${styles.app}` : styles.root}>
      {lockedItem && (
        <LockedRoyaleItem name={lockedItem.label} preview={<HeadPreview type={lockedItem.id as HeadStyleType} />}
          onClose={() => setLockedItem(null)} onJoin={() => { setLockedItem(null); navigate("/royale"); }} />
      )}
      {unlockItem && (
        <UnlockMoment name={unlockItem.label} preview={<HeadPreview type={unlockItem.id as HeadStyleType} />}
          onClose={() => { markUnlockSeen(unlockItem.id); setUnlockItem(null); }}
          onEquip={() => { const it = unlockItem; markUnlockSeen(it.id); setUnlockItem(null); const act = tileAction(it); if (act) act(); }} />
      )}
      {/* The department tab band (shared with Profile). The egg balance sits up
          in the header beside the title (via the modal's head slot). On a
          landscape phone the departments are a rail down the left instead. */}
      {app ? (
        <nav className={styles.rail} aria-label="Shop departments">
          {SHOP_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={[styles.railBtn, tab === t.id ? styles.railOn : "", t.id === "packs" ? styles.railGold : ""].filter(Boolean).join(" ")}
              aria-pressed={tab === t.id}
              onClick={() => { setPending(null); selectTab(t.id); shelfRef.current?.scrollTo({ top: 0 }); }}
            >
              {RAIL_ICONS[t.id]}<span>{t.label}</span>
              {t.id === "heads" && newUnlocks.some((u) => u.kind === "head") ? <b className={styles.railCount}>{newUnlocks.filter((u) => u.kind === "head").length}</b> : null}
            </button>
          ))}
          {fakeMode ? <span className={`${styles.hchip} ${styles.hchipInfo}`}>TEST MODE</span> : null}
          {freeEquip ? <span className={`${styles.hchip} ${styles.hchipInfo}`}>FREE EQUIP</span> : null}
        </nav>
      ) : (
        <SectionTabs tabs={SHOP_TABS.map((t) => (t.id === "heads" && newUnlocks.some((u) => u.kind === "head") ? { ...t, badge: newUnlocks.filter((u) => u.kind === "head").length } : t))} value={tab} onChange={selectTab} />
      )}

      {/* A landscape phone confirms in a sheet from the right, over the shelf:
          the item big, "Try it first", and the buy button with its price. */}
      {pending && app ? (
        <>
          <button type="button" className={styles.sheetScrim} onClick={() => setPending(null)} aria-label="Close" />
          <aside className={styles.sheet} key={pending.id} style={{ "--c": RARITY_COLOR[pending.rarity || "common"] } as React.CSSProperties}>
            <button type="button" className={styles.sheetX} onClick={() => setPending(null)} aria-label="Close">
              <svg {...railSvg}><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
            <div className={pending.kind === "board" ? `${styles.sheetArt} ${styles.sheetArtBare}` : styles.sheetArt}>
              {pending.kind === "head" ? <HeadPreview type={pending.id as HeadStyleType} />
                : pending.kind === "board" ? <BoardPreview id={pending.id} size={118} />
                : pending.kind === "flag" ? <img src={flagUrlSquare(pending.id)} alt={flagName(pending.id)} className={styles.flagImg} />
                : <VictoryPreview type={pending.id as VictoryAnimType} flag={equippedFlag} />}
            </div>
            <i className={styles.sheetRarity}>{RARITIES[pending.rarity || "common"]?.label}</i>
            <h3>{pending.label}</h3>
            <p>{confirmBlurb(pending)}</p>
            <div className={styles.sheetBtns}>
              {pending.kind === "head" || pending.kind === "board" || pending.kind === "victory" ? (
                <button type="button" className={styles.sheetTry} onClick={() => navigate(`/shop/try/${pending.kind}/${encodeURIComponent(pending.id)}`)}>
                  <svg {...railSvg}><path d="M7 5l12 7-12 7V5z" fill="currentColor" fillOpacity="0.25" /></svg>
                  Try it first
                </button>
              ) : null}
              {short(pending) > 0 ? (
                <button type="button" className={styles.sheetBuy} onClick={() => { setPending(null); selectTab("packs"); }}>
                  Get {short(pending).toLocaleString()} more <img src={eggIconUrl()} alt="" />
                </button>
              ) : (
                <button type="button" className={styles.sheetBuy} onClick={() => { const it = pending; setPending(null); buy(it); }}>
                  Buy for <img src={eggIconUrl()} alt="" />{eggCost(pending).toLocaleString()}
                </button>
              )}
            </div>
          </aside>
        </>
      ) : pending ? (
        <Modal title="Confirm Purchase" onClose={() => setPending(null)} className={styles.confirm} closeButton={false}>
          <div className={styles.confirmBody}>
            <div className={styles.confirmText}>
              <p className={styles.confirmName}>{pending.label}</p>
              <p className={styles.confirmBlurb}>{confirmBlurb(pending)}</p>
              <p className={styles.confirmPrice}>
                <img src={eggIconUrl()} alt="" className={styles.eggIcon} />
                {eggCost(pending).toLocaleString()}
              </p>
            </div>
            <div className={styles.confirmSide}>
              <div className={pending.kind === "board" ? styles.confirmWellBare : styles.confirmWell}>
                {pending.kind === "head" ? <HeadPreview type={pending.id as HeadStyleType} />
                  : pending.kind === "board" ? <BoardPreview id={pending.id} size={118} />
                  : pending.kind === "flag" ? <img src={flagUrlSquare(pending.id)} alt={flagName(pending.id)} className={styles.flagImg} />
                  : <VictoryPreview type={pending.id as VictoryAnimType} flag={equippedFlag} />}
              </div>
              {pending.kind === "head" || pending.kind === "board" || pending.kind === "victory" ? (
                <button className={styles.confirmTry} onClick={() => navigate(`/shop/try/${pending.kind}/${encodeURIComponent(pending.id)}`)}>
                  Try it first
                </button>
              ) : null}
            </div>
          </div>
          <div className={styles.confirmActions}>
            <button className={styles.confirmCancel} onClick={() => setPending(null)}>Cancel</button>
            <button className={styles.confirmBuy} onClick={() => { const it = pending; setPending(null); buy(it); }}>Confirm</button>
          </div>
        </Modal>
      ) : null}

      {/* (No box of its own on desktop; on a landscape phone it is the
          scrolling shelf beside the rail.) */}
      <div className={styles.shelf} ref={shelfRef}>
      {probablySignedIn && claimable.map((r) => <ClaimStrip key={r.id} r={r} onClaim={() => setClaiming(r)} />)}
      {claiming && (
        <ClaimReveal r={claiming}
          onClaimed={(got) => { refreshOwned(); if (got.item) markUnlockSeen(got.item.id); }}
          onClose={() => setClaiming(null)}
          onWear={claiming.item && claiming.item.kind === "head" ? () => {
            const it = EXCLUSIVE_ITEMS.find((i) => i.kind === "head" && i.id === claiming.item!.id);
            if (it) equipHead(it);
            setClaiming(null);
          } : undefined} />
      )}

      {tab === "packs" && (
        <>
          <h3 className={styles.h}>
            Golden Eggs
            <span className={styles.hSub}>This is the game's currency, 'cause why not?</span>
          </h3>
          <div className={`${styles.eggGrid} ${styles.packGrid}`}>
            {EGG_PACKS.map((pack, i) => (
              <EggPackCard key={pack.id} pack={pack} index={i} busy={busyId === pack.id} onBuy={() => buy(pack)} />
            ))}
          </div>
        </>
      )}

      {tab === "heads" && (
        <>
          <h3 className={styles.h}>
            Worm Heads
            <span className={styles.hSub}>Who cares about where the hitbox is? Looks matter.</span>
          </h3>
          <div className={styles.eggGrid}>
            {royaleHeads.map((item) => {
              // won, waiting to be claimed: its card is the claim too
              const toClaim = probablySignedIn && !isOwned(item) ? claimable.find((r) => r.item && r.item.id === item.id) : undefined;
              if (toClaim) {
                return (
                  <div key={item.id} className={styles.newUnlock} data-sound="none">
                    <span className={styles.newTag}>Won</span>
                    <ShopCard dataId={item.id} title={item.label} tier="tierGold" lvl="lvl4" frameClass={styles.cardRoyale}
                      visual={<HeadPreview type={item.id as HeadStyleType} />} onClick={() => setClaiming(toClaim)}
                      actions={<span className={styles.buyWrap}><button type="button" className={styles.buy} onClick={(e) => { e.stopPropagation(); setClaiming(toClaim); }}>Claim</button></span>} />
                  </div>
                );
              }
              return isOwned(item) ? (
              // won and not seen yet: it glows, and opening it plays its unlock once
              newUnlocks.some((u) => u.id === item.id)
                ? <div key={item.id} className={styles.newUnlock} data-sound="none" onClickCapture={(e) => { e.stopPropagation(); e.preventDefault(); setUnlockItem(item); }}><span className={styles.newTag}>New</span>{headCard(item)}</div>
                : headCard(item)
            ) : (
              // not won: the "denied" sound and how it is earned (data-sound: no click on top)
              <div key={item.id} className={styles.lockedWrap} data-sound="none">
                <ShopCard
                  dataId={item.id}
                  title={item.label}
                  tier="tierGold"
                  lvl="lvl4"
                  frameClass={styles.cardRoyale}
                  visual={<HeadPreview type={item.id as HeadStyleType} />}
                  onClick={() => setLockedItem(item)}
                  actions={<span className={styles.buyWrap}><span className={styles.winIt}>Win a Royale</span></span>}
                />
              </div>
            );
            })}
            {headItems.map(headCard)}
          </div>
        </>
      )}

      {tab === "arena" && (
        <>
          <h3 className={styles.h}>
            Arena
            <span className={styles.hSub}>The Arena is for your eyes only. Unless you host a game yourself!</span>
          </h3>
          <div className={styles.eggGrid}>
            {boardItems.map((item) => <CosmeticCard key={item.id} item={item} onClick={tileAction(item)} equipped={isEquippedItem(item)} actions={tileActions(item)} renderPreview={() => <BoardPreview id={item.id} />} />)}
          </div>
        </>
      )}

      {tab === "flags" && (
        <>
          <h3 className={styles.h}>
            Flags
            <span className={styles.hSub}>Instead of being a nationalist, you can be... you.</span>
          </h3>
          <div className={styles.eggGrid}>
            {flagItems.map((item) => (
              <CosmeticCard key={item.id} item={item} onClick={tileAction(item)} equipped={isEquippedItem(item)} actions={tileActions(item)}
                renderPreview={() => <img src={flagUrlSquare(item.id)} alt={flagName(item.id)} className={styles.flagImg} />} />
            ))}
          </div>
        </>
      )}

      {tab === "victory" && (
        <>
          <h3 className={styles.h}>
            Victory Celebrations
            <span className={styles.hSub}>What's a victory without shoving it in everyone's face?</span>
          </h3>
          <div className={styles.eggGrid}>
            {victoryItems.map((item) => <CosmeticCard key={item.id} item={item} onClick={tileAction(item)} equipped={isEquippedItem(item)} actions={tileActions(item)} renderPreview={() => <VictoryPreview type={item.id as VictoryAnimType} flag={equippedFlag} />} />)}
          </div>
        </>
      )}
      </div>
      {app && banner ? <div className={styles.toast} role="status">{banner.text}</div> : null}
      {showLogin ? <LoginModal reason="You need to sign in to access the shop." dim onClose={() => setShowLogin(false)} /> : null}
    </div>
  );
}
